import { useBabies } from './queries';
import type { Baby } from './types';
import type { BabyAccessRole } from './dto';

/**
 * What the current user may do with a baby, derived from their access role.
 * Mirrors the backend gates exactly:
 *   - canWrite     (owner/editor): milestone status, growth, activity state,
 *                  baby edit, media delete
 *   - canInteract  (owner/editor/contributor): comments + reactions
 *   - canUploadMedia (owner/editor, or contributor with the upload flag): media
 *
 * An unknown role (e.g. fixtures or before /babies resolves) is treated as
 * full access so we never wrongly hide controls from the legitimate owner.
 */
export interface Caps {
  role: BabyAccessRole | undefined;
  canWrite: boolean;
  canInteract: boolean;
  canUploadMedia: boolean;
  isViewer: boolean;
}

export function capsFor(role: BabyAccessRole | undefined, canUploadMedia?: boolean | 0 | 1): Caps {
  const restricted = role === 'contributor' || role === 'viewer';
  const canWrite = !restricted; // owner / editor / unknown
  return {
    role,
    canWrite,
    canInteract: role !== 'viewer',
    canUploadMedia: canWrite || canUploadMedia === true || canUploadMedia === 1,
    isViewer: role === 'viewer',
  };
}

/** Capabilities from a UI Baby view-model (which already carries role). */
export function babyCaps(baby?: Baby | null): Caps {
  return capsFor(baby?.role, baby?.canUploadMedia);
}

/**
 * Capabilities for a baby id, resolved from the cached /babies list. For
 * components that only have a babyId (EngagementBar, MilestoneMemories) rather
 * than the full Baby view-model.
 */
export function useBabyCaps(babyId: string | null | undefined): Caps {
  const { data } = useBabies();
  const row = data?.find((b) => b.id === babyId);
  return capsFor(row?.role, row?.can_upload_media);
}
